import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpResponse
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, first, mergeMap, tap } from 'rxjs/operators';

import { NavigationService } from '../services';
import { AuthFacade } from '@cpd-app/state/auth';
import { environment } from '@cpd-app/environment';
import { ErrorCodeType } from '@cpd-app/shared-module/constants';
import { resolveGraphQlStatusCode } from './graphql-error-status';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private navigationService: NavigationService,
    private authFacade: AuthFacade
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    let addToken = true;

    if (
      (request.headers.has('No-token') &&
        request.headers.get('No-token') === 'true') ||
      request.url.includes('assets/images') ||
      request.url.includes('http://') ||
      request.url.includes('https://')
    ) {
      addToken = false;
    }

    // Callers can opt out of the global error-page redirect for endpoints
    // that surface their own inline error UI (e.g. the JST upload panel
    // shows parse failures in place rather than nuking the whole page).
    // The header is stripped before the request leaves the client.
    const skipErrorRedirect =
      request.headers.get('X-Skip-Error-Redirect') === 'true';

    return this.authFacade.authToken$.pipe(
      first(),
      mergeMap((token) => {
        const authReq = addToken
          ? request.clone({
              url: environment.apiUrlBase + request.url,
              setHeaders: {
                Token: `${token}`
              },
              headers: request.headers.delete('X-Skip-Error-Redirect')
            })
          : (request = request.clone({
              url: request.url,
              headers: request.headers
                .delete('No-token')
                .delete('X-Skip-Error-Redirect')
            }));

        return next.handle(authReq).pipe(
          tap((response) => {
            if (
              response instanceof HttpResponse &&
              response?.body?.errors?.length
            ) {
              const statusCode = resolveGraphQlStatusCode(
                response?.body.errors[0]
              );

              // Never navigate on an unclassified error: an unrecognised code
              // reaches the error page as `undefined` and renders as a 500.
              // 403 is refreshable and handled by SessionInterceptor.
              if (
                !skipErrorRedirect &&
                statusCode &&
                statusCode !== ErrorCodeType.SessionExpired
              ) {
                this.navigationService.goToErrorPage(statusCode);
              }
            }
          }),
          catchError((response) => {
            /*
             * A 400 FROM THE GRAPHQL ENDPOINT IS A BAD DOCUMENT, NOT A BAD
             * ROUTE. The same rule SessionInterceptor applies, repeated here
             * because Apollo's requests pass through BOTH interceptors and
             * each decides independently: fixing only the other one left this
             * branch still replacing the page with /error/400.
             *
             * The failure still propagates to the caller and is still logged;
             * what it no longer does is take down the page around it.
             */
            // Matched on the path: `environment.graphQlUri` is null in some
            // environments and `includes(null)` never matches, which would
            // switch the rule off silently.
            const isBadGraphQlDocument =
              response instanceof HttpErrorResponse &&
              response.status === 400 &&
              /\/graphql\b/.test(response.url || authReq.url || '');

            if (
              response instanceof HttpErrorResponse &&
              response.url !== environment.streamUri &&
              !skipErrorRedirect &&
              !isBadGraphQlDocument
            ) {
              this.navigationService.goToErrorPage(
                (response?.status || ErrorCodeType.ServerError) + ''
              );
            }

            return throwError(response);
          })
        );
      })
    );
  }
}
